/**
 * Send notices — the review screen for a batch (decision 0006, Bettina
 * 2026-10-29, on David Gusewelle's recommendation of one email per audit run).
 *
 * Opening it drafts the batch for the selected cases: one servicing notice per
 * servicing franchisee and one territory-owner notice per territory owner.
 * Everything in the selection that can't go is listed with the reason (not
 * reviewed, own territory, held for a missing Amada email, …). Any notice can
 * be opened, previewed and edited; Send all sends them all. A failed notice is
 * shown in red with Retry, and its cases stay Reviewed until both sides go.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { ConfirmActionModal } from './ConfirmActionModal';
import { NoticeEditor } from './NoticeEditor';
import { api, ApiError } from '../lib/api';

interface Exclusion { case_id: number; kind: string; reason: string }
interface BatchNotice {
  id: number;
  party: 'offending' | 'neighbor';
  party_af: string;
  to_emails: string[];
  cc_emails: string[];
  subject: string | null;
  body_html: string | null;
  draft_warnings: string[];
  status: string;
  test_mode: boolean;
  send_error: string | null;
  sent_at: string | null;
  cases: { id: number; state: string; franchisee_af: string; owner_af: string | null; zip: string | null }[];
}
interface Batch { id: number; excluded: Exclusion[]; notices: BatchNotice[]; sent_at: string | null }
interface SendResult { sent: number[]; failed: { id: number; party: string; party_af: string; error: string }[]; notified: number[]; test_mode: boolean; no_fbc?: number[] }

const ROLE: Record<string, string> = { offending: 'Servicing notice', neighbor: 'Territory-owner notice' };
const KIND_LABEL: Record<string, string> = {
  held_unreachable: 'Held — no Amada email',
  partly_notified: 'One side already notified',
  not_reviewed: 'Not reviewed yet',
  not_open: 'Already notified or closed',
  own_territory: 'Own territory — not in a territory notice',
  no_owner: 'No territory owner — not in a territory notice',
  not_found: 'Not found',
};
const KIND_ORDER = ['held_unreachable', 'partly_notified', 'not_reviewed', 'not_open', 'own_territory', 'no_owner', 'not_found'];

const errText = (e: unknown, fallback: string) => {
  const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
  return body?.error ?? (e instanceof Error ? e.message : fallback);
};
const caseLink = (id: number) => (
  <a key={id} href={`${window.location.origin}/?case=${id}`} target="_blank" rel="noreferrer" className="mono">#{id}</a>
);

export function BatchNotices({ caseIds, onClose, onSent }: { caseIds: number[]; onClose: () => void; onSent: () => void }) {
  const [batch, setBatch] = useState<Batch | null>(null);
  const [excluded, setExcluded] = useState<Exclusion[]>([]);
  const [mode, setMode] = useState<{ mode: 'test' | 'live'; test_recipients: string[]; bcc?: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  useEffect(() => {
    Promise.all([
      api<{ batch: Batch | null; excluded: Exclusion[] }>('/api/notifications/batches', { method: 'POST', body: { case_ids: caseIds } }),
      api<{ mode: 'test' | 'live'; test_recipients: string[]; bcc?: string[] }>('/api/notifications/mode'),
    ])
      .then(([d, m]) => { setBatch(d.batch); setExcluded(d.batch?.excluded ?? d.excluded); setMode(m); setTestTo(m.test_recipients.join(', ')); })
      .catch((e) => setError(errText(e, 'Could not draft the notices')))
      .finally(() => setLoading(false));
  }, [caseIds]);

  const reload = async () => {
    if (!batch) return;
    const d = await api<{ batch: Batch }>(`/api/notifications/batches/${batch.id}`);
    setBatch(d.batch);
  };

  const sendAll = async () => {
    if (!batch) return;
    setSending(true); setError(null);
    try {
      const list = testTo.split(',').map((x) => x.trim()).filter(Boolean);
      const r = await api<SendResult & { batch: Batch }>(`/api/notifications/batches/${batch.id}/send`, {
        method: 'POST', body: mode?.mode === 'test' ? { test_recipients: list } : {},
      });
      setResult(r); setBatch(r.batch);
      onSent();
    } catch (e) { setError(errText(e, 'Send failed')); } finally { setSending(false); }
  };

  const retry = async (n: BatchNotice) => {
    setError(null);
    try {
      const list = testTo.split(',').map((x) => x.trim()).filter(Boolean);
      await api(`/api/notifications/${n.id}/send`, { method: 'POST', body: n.test_mode ? { test_recipients: list } : {} });
      await reload();
      onSent();
    } catch (e) { setError(`${n.party_af} ${ROLE[n.party].toLowerCase()}: ${errText(e, 'Retry failed')}`); await reload(); }
  };

  const groups = KIND_ORDER.map((k) => ({ kind: k, rows: excluded.filter((x) => x.kind === k) })).filter((g) => g.rows.length);
  const notices = batch?.notices ?? [];
  const unsent = notices.filter((n) => n.status === 'draft' || n.status === 'failed');
  const failed = notices.filter((n) => n.status === 'failed');
  const warnCount = notices.filter((n) => n.draft_warnings.length).length;
  const inNotices = new Set(notices.flatMap((n) => n.cases.map((c) => c.id))).size;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="scroll-y" style={{
        background: 'var(--surface)', borderRadius: 8, padding: 20, width: 'min(1100px, 94vw)', maxHeight: '90vh',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Send notices{batch ? <span className="muted" style={{ fontWeight: 400 }}> · batch #{batch.id}</span> : null}</h2>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
              {caseIds.length} case{caseIds.length === 1 ? '' : 's'} selected · {inNotices} in {notices.length} notice{notices.length === 1 ? '' : 's'} · {excluded.length} not included
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
        </div>

        {mode?.mode === 'test' && (
          <div style={{ border: '1px dashed var(--warn)', background: 'var(--accent-soft)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginTop: 12 }}>
            <strong>TEST MODE</strong> — Send all delivers every notice only to the test addresses, with the real recipients in a banner. No franchisee receives anything and no case changes state. New cases are included for testing; live sends take Reviewed cases only.
          </div>
        )}
        {mode && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            BCC on every live notice: {mode.bcc && mode.bcc.length ? <span className="mono">{mode.bcc.join(', ')}</span> : 'none'}
            <span className="faint"> · set on Configuration → Notice BCC addresses{mode.mode === 'test' ? ' · test sends are not BCC\'d' : ''}</span>
          </div>
        )}
        {loading && <div className="muted" style={{ fontSize: 13, marginTop: 16 }}>Drafting notices…</div>}
        {error && <div className="card card-pad c-danger" style={{ fontSize: 12.5, marginTop: 12 }}>{error}</div>}

        {result && (
          <div className="card card-pad" style={{ marginTop: 12, fontSize: 13, background: result.failed.length ? 'var(--danger-soft, var(--warn-soft))' : 'var(--ok-soft)' }}>
            <strong>{result.sent.length} notice{result.sent.length === 1 ? '' : 's'} sent{result.test_mode ? ' (test)' : ''}, {result.failed.length} failed.</strong>
            {!result.test_mode && <> {result.notified.length} case{result.notified.length === 1 ? '' : 's'} moved to Notified and assigned to the servicing partner's FBC.</>}
            {result.no_fbc && result.no_fbc.length > 0 && (
              <div className="c-warn" style={{ marginTop: 4 }}>
                Not assigned — no FBC in Zoho for the servicing partner: {result.no_fbc.map((id, i) => <span key={id}>{i > 0 ? ', ' : ''}{caseLink(id)}</span>)}. Assign these by hand.
              </div>
            )}
            {result.failed.length > 0 && <div style={{ marginTop: 4 }}>Cases on a failed notice stay Reviewed until it is retried — use <b>Retry</b> below.</div>}
          </div>
        )}

        {groups.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 6 }}>Not included in this send</div>
            {groups.map((g) => (
              <div key={g.kind} className={g.kind === 'held_unreachable' || g.kind === 'partly_notified' ? 'c-danger' : undefined} style={{ fontSize: 12.5, marginBottom: 6 }}>
                <strong>{KIND_LABEL[g.kind] ?? g.kind} · {g.rows.length}</strong>
                {g.kind === 'held_unreachable' || g.kind === 'partly_notified' ? (
                  <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                    {g.rows.map((x) => <li key={x.case_id}>{caseLink(x.case_id)} — {x.reason}</li>)}
                  </ul>
                ) : (
                  <span className="muted"> — {g.rows.slice(0, 40).map((x, i) => <span key={x.case_id}>{i > 0 ? ', ' : ''}{caseLink(x.case_id)}</span>)}{g.rows.length > 40 ? ` and ${g.rows.length - 40} more` : ''}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && !batch && !error && (
          <div className="card card-pad muted" style={{ fontSize: 13, marginTop: 14 }}>Nothing in this selection can be sent — see the reasons above. Only Reviewed cases in another partner's territory go in a notice.</div>
        )}

        {batch && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 6 }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                Notices · {notices.length}{warnCount ? <span className="c-warn" style={{ textTransform: 'none', letterSpacing: 0 }}> · {warnCount} with warnings</span> : null}
              </div>
              <button className="btn btn-danger" disabled={sending || unsent.length === 0} onClick={() => setConfirming(true)}>
                <Icon name="bell" size={13} /> {sending ? 'Sending…' : failed.length && unsent.length === failed.length ? `Retry ${failed.length} failed` : `Send all (${unsent.length})`}
              </button>
            </div>
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead><tr><th>Notice</th><th>To</th><th>CC</th><th>Cases</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {notices.map((n) => (
                    <NoticeRow key={n.id} n={n} open={openId === n.id} onToggle={() => setOpenId(openId === n.id ? null : n.id)}
                               onSaved={reload} onRetry={() => retry(n)} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {confirming && batch && (
          <ConfirmActionModal
            title={mode?.mode === 'test' ? `Send ${unsent.length} test notice${unsent.length === 1 ? '' : 's'}` : `Send ${unsent.length} notice${unsent.length === 1 ? '' : 's'} to franchisees`}
            body={mode?.mode === 'test'
              ? <>Test mode: every notice goes ONLY to the addresses below (you always get a copy), with the real recipients in a banner.
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>Send test to</label>
                    <input className="input" value={testTo} onChange={(e) => setTestTo(e.target.value)} style={{ width: '100%' }} />
                  </div></>
              : <>This sends <b>{unsent.length} live, franchisee-facing notice{unsent.length === 1 ? '' : 's'}</b>, which cannot be recalled. Each case moves to <b>Notified</b> once both of its notices have gone.</>}
            tone={mode?.mode === 'test' ? 'warn' : 'danger'}
            confirmLabel={mode?.mode === 'test' ? 'Send test notices' : 'Send live notices'}
            reasonField="none"
            onConfirm={async () => { setConfirming(false); await sendAll(); }}
            onClose={() => setConfirming(false)}
          />
        )}
      </div>
    </div>
  );
}

function NoticeRow({ n, open, onToggle, onSaved, onRetry }: {
  n: BatchNotice; open: boolean; onToggle: () => void; onSaved: () => Promise<void>; onRetry: () => void;
}) {
  const editable = n.status === 'draft' || n.status === 'failed';
  const [to, setTo] = useState(n.to_emails.join(', '));
  const [cc, setCc] = useState(n.cc_emails.join(', '));
  const [subject, setSubject] = useState(n.subject ?? '');
  const [body, setBody] = useState(n.body_html ?? '');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/notifications/${n.id}`, { method: 'PUT', body: { to_emails: list(to), cc_emails: list(cc), subject, body_html: body } });
      setDirty(false);
      await onSaved();
    } catch (e) { setErr(errText(e, 'Save failed')); } finally { setBusy(false); }
  };

  const statusClass = ['sent', 'delivered', 'opened'].includes(n.status) ? 'c-ok' : n.status === 'failed' || n.status === 'bounced' ? 'c-danger' : 'muted';
  return (
    <>
      <tr>
        <td>
          <div style={{ fontWeight: 600 }} className="mono">{n.party_af}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{ROLE[n.party]}</div>
          {n.draft_warnings.map((w, i) => <div key={i} className="c-warn" style={{ fontSize: 11.5, marginTop: 2, maxWidth: 360 }}>⚠ {w}</div>)}
        </td>
        <td className="mono" style={{ fontSize: 11.5, maxWidth: 220 }}>{n.to_emails.join(', ') || <span className="c-danger">none</span>}</td>
        <td className="mono" style={{ fontSize: 11.5, maxWidth: 200 }}>{n.cc_emails.join(', ') || '—'}</td>
        <td style={{ fontSize: 12 }}>{n.cases.length} · {n.cases.slice(0, 6).map((c, i) => <span key={c.id}>{i > 0 ? ', ' : ''}{caseLink(c.id)}</span>)}{n.cases.length > 6 ? '…' : ''}</td>
        <td className={statusClass} style={{ fontSize: 12, fontWeight: 600 }}>
          {n.status}{n.sent_at && n.test_mode ? ' (test)' : ''}
          {n.send_error && <div style={{ fontWeight: 400, fontSize: 11, maxWidth: 220 }}>{n.send_error}</div>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-sm btn-ghost" onClick={onToggle}><Icon name="eye" size={12} /> {open ? 'Close' : editable ? 'Preview / edit' : 'View'}</button>
          {n.status === 'failed' && <button className="btn btn-sm btn-danger" onClick={onRetry}><Icon name="refresh" size={12} /> Retry</button>}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg)' }}>
            {err && <div className="c-danger" style={{ fontSize: 12, marginBottom: 6 }}>{err}</div>}
            {editable ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
                <label style={{ fontSize: 11.5 }} className="muted">To (all owners)<input className="input" value={to} onChange={(e) => { setTo(e.target.value); setDirty(true); }} /></label>
                <label style={{ fontSize: 11.5 }} className="muted">CC (FBC + AFI)<input className="input" value={cc} onChange={(e) => { setCc(e.target.value); setDirty(true); }} /></label>
                <label style={{ fontSize: 11.5 }} className="muted">Subject<input className="input" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} /></label>
                <NoticeEditor html={body} onChange={(h) => { setBody(h); setDirty(true); }} minHeight={220} />
                <div><button className="btn btn-sm" disabled={!dirty || busy} onClick={save}><Icon name="check" size={12} /> {busy ? 'Saving…' : 'Save changes'}</button>
                  <span className="muted" style={{ fontSize: 11.5, marginLeft: 8 }}>Dates such as the notice date are filled in when sent.</span></div>
              </div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '12px 16px', fontSize: 13, lineHeight: 1.55, background: 'var(--surface)' }}
                   dangerouslySetInnerHTML={{ __html: n.body_html || '<em>(empty)</em>' }} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}
